import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Toast, ConfirmModal } from '../../../shared/components';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { seatTypesService } from '../../../shared/services/seatTypesService';
import { useConfirm } from '../../../shared/hooks';

const SeatConfigModal = ({ isOpen, onClose, eventId, eventTitle, onSuccess }) => {
  const [seatTypes, setSeatTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  // Hook para confirmaciones
  const { confirmState, confirm, closeConfirm } = useConfirm();

  // Formulario para crear/editar tipo de asiento
  const [formData, setFormData] = useState({
    type: '',
    description: '',
    price: 0,
    category: 'section',
    section: '',
    startRow: 1,
    endRow: 1,
    priority: 1,
    isActive: true,
    color: 'bg-amber-600'
  });

  // Cargar tipos de asientos del evento
  const loadSeatTypes = useCallback(async () => {
    setLoading(true);
    try {
      const types = await seatTypesService.getSeatTypesByEvent(eventId);
      setSeatTypes(types);
    } catch (error) {
      showToast('Error al cargar los tipos de asientos', 'error');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Crear tipos por defecto
  const createDefaultTypes = async () => {
    setSaving(true);
    try {
      await seatTypesService.createDefaultSeatTypes(eventId);
      await loadSeatTypes();
      showToast('Tipos de asientos por defecto creados exitosamente', 'success');
    } catch (error) {
      showToast('Error al crear tipos por defecto', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Guardar tipo de asiento
  const saveSeatType = async () => {
    if (!formData.type || !formData.description || formData.price <= 0) {
      showToast('Por favor completa todos los campos requeridos', 'error');
      return;
    }

    // Para rangos de filas, generar un tipo único basado en el rango
    let finalType = formData.type;
    if (formData.category === 'row_range') {
      finalType = `${formData.type}_fila_${formData.startRow}_${formData.endRow}`;
    }

    // Para sección completa, usar el sector como sección
    // Para rangos de filas, también usar el sector como sección
    let finalSection = formData.type; // Siempre usar el sector seleccionado

    setSaving(true);
    try {
      const seatTypeData = {
        ...formData,
        type: finalType,
        section: finalSection,
        eventId
      };

      if (editingType) {
        await seatTypesService.updateSeatType(editingType.id, seatTypeData);
        showToast('Tipo de asiento actualizado exitosamente', 'success');
      } else {
        await seatTypesService.createSeatType(seatTypeData);
        showToast('Tipo de asiento creado exitosamente', 'success');
      }
      
      await loadSeatTypes();
      resetForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      showToast(error.message || 'Error al guardar el tipo de asiento', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Eliminar tipo de asiento
  const deleteSeatType = async (typeId) => {
    const confirmed = await confirm({
      title: 'Confirmar Eliminación',
      message: '¿Estás seguro de que quieres eliminar este tipo de asiento? Esta acción no se puede deshacer.',
      type: 'danger',
      confirmText: 'Eliminar Tipo',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        await seatTypesService.deleteSeatType(typeId);
        await loadSeatTypes();
        showToast('Tipo de asiento eliminado exitosamente', 'success');
        if (onSuccess) onSuccess();
      }
    });
    
    if (!confirmed) {
      showToast('Operación cancelada', 'warning');
    }
  };

  // Función para limpiar el precio si viene con formato de moneda
  const cleanPrice = (price) => {
    if (!price) return price;
    
    // Si es un string con formato de moneda (ej: "5500.00"), convertirlo a número
    if (typeof price === 'string' && /^\d+\.00$/.test(price)) {
      return parseFloat(price);
    }
    
    // Si es un string numérico, convertirlo a número
    if (typeof price === 'string' && /^\d+(\.\d+)?$/.test(price)) {
      return parseFloat(price);
    }
    
    return price;
  };

  // Editar tipo de asiento
  const editSeatType = (type) => {
    setEditingType(type);
    
    // Extraer el tipo base si es un rango de filas
    let baseType = type.type;
    if (type.category === 'row_range' && type.type.includes('_fila_')) {
      baseType = type.type.split('_fila_')[0];
    }
    
    setFormData({
      type: baseType,
      description: type.description,
      price: cleanPrice(type.price),
      category: type.category,
      section: type.section || '',
      startRow: type.startRow || 1,
      endRow: type.endRow || 1,
      priority: type.priority || 1,
      isActive: type.isActive,
      color: type.color || 'bg-amber-600'
    });
    setShowCreateForm(true);
  };

  // Resetear formulario
  const resetForm = () => {
    setFormData({
      type: '',
      description: '',
      price: 0,
      category: 'section',
      section: '',
      startRow: 1,
      endRow: 1,
      priority: 1,
      isActive: true,
      color: 'bg-amber-600'
    });
    setEditingType(null);
    setShowCreateForm(false);
  };

  // Mostrar notificación
  const showToast = (message, type = 'success') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  // Ocultar notificación
  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Cargar tipos cuando se abre el modal
  useEffect(() => {
    if (isOpen && eventId) {
      loadSeatTypes();
    }
  }, [isOpen, eventId, loadSeatTypes]);

  // Cerrar modal
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getCategoryLabel = (category) => {
    const labels = {
      section: 'Sección',
      row_range: 'Rango de Filas',
      box: 'Palco',
      palco: 'Palco'
    };
    return labels[category] || category;
  };

  const getSectionLabel = (section) => {
    const labels = {
      planta_baja: 'Planta Baja',
      primer_piso: 'Primer Piso',
      palco: 'Palco'
    };
    return labels[section] || section;
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={`Configuración de Asientos - ${eventTitle}`}
        size="lg"
      >
        <div className="space-y-6">
          {/* Header con botones de acción */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Tipos de Asientos
              </h3>
              <p className="text-sm text-gray-600">
                Configura los precios y tipos de asientos para este evento
              </p>
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={() => setShowCreateForm(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Tipo
              </Button>
            </div>
          </div>

          {/* Lista de tipos de asientos */}
          {!showCreateForm && (
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Cargando tipos de asientos...</p>
                </div>
              ) : seatTypes.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-600 mb-4">No hay tipos de asientos configurados</p>
                  <Button
                    onClick={createDefaultTypes}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Crear Tipos por Defecto
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {seatTypes.map((type) => (
                    <div
                      key={type.id}
                      className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="w-4 h-4 rounded-full bg-purple-500"></div>
                          <div>
                            <h4 className="font-medium text-gray-900">{type.description}</h4>
                                                         <p className="text-sm text-gray-600">
                               {getCategoryLabel(type.category)}
                               {type.section && type.category !== 'palco' && ` - ${getSectionLabel(type.section)}`}
                               {type.category === 'row_range' && type.startRow && type.endRow && ` (Filas ${type.startRow}-${type.endRow})`}
                               {type.type === 'palco' && ' - Palco'}
                             </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-lg font-bold text-green-600">
                          ${type.price}
                        </span>
                        <div className="flex space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => editSeatType(type)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteSeatType(type.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Formulario para crear/editar */}
          {showCreateForm && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold">
                  {editingType ? 'Editar Tipo de Asiento' : 'Nuevo Tipo de Asiento'}
                </h4>
                <Button
                  variant="ghost"
                  onClick={resetForm}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Sector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sector*
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Seleccionar Sector</option>
                    <option value="planta_baja">Planta Baja</option>
                    <option value="primer_piso">Primer Piso</option>
                    <option value="palco">Palco</option>
                  </select>
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción*
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="ej: Planta Baja Premium, Filas 1-10"
                  />
                </div>

                {/* Tipo (Sector, Rango de Filas) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo*
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="section">Sector Completo</option>
                    <option value="row_range">Rango de Filas</option>
                    {/* <option value="box">Palco</option> */}
                  </select>
                </div>



                {formData.category === 'row_range' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fila Inicial
                      </label>
                      <input
                        type="number"
                        value={formData.startRow}
                        onChange={(e) => setFormData({ ...formData, startRow: parseInt(e.target.value) || 1 })}
                        // Sin esto, la ruedita o el pad cambian el número al hacer scroll sobre el campo
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fila Final
                      </label>
                      <input
                        type="number"
                        value={formData.endRow}
                        onChange={(e) => setFormData({ ...formData, endRow: parseInt(e.target.value) || 1 })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        min="1"
                      />
                    </div>
                  </div>
                )}

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color*
                  </label>
                  <select
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="bg-amber-600">Ámbar</option>
                    <option value="bg-purple-500">Púrpura</option>
                    <option value="bg-yellow-400">Amarillo</option>
                    <option value="bg-red-500">Rojo</option>
                    <option value="bg-green-500">Verde</option>
                    <option value="bg-pink-500">Rosa</option>
                    <option value="bg-cyan-500">Cian</option>
                    <option value="bg-indigo-500">Índigo</option>
                    <option value="bg-lime-500">Lima</option>
                    <option value="bg-fuchsia-500">Fucsia</option>
                  </select>
                </div>

                {/* Precio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio*
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={resetForm}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={saveSeatType}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal de confirmación */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        type={confirmState.type}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        loading={confirmState.loading}
      />

      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </>
  );
};

export default SeatConfigModal; 